import * as Sentry from "@sentry/react";

// Initialize Sentry for error tracking
export const initSentry = () => {
  // Only initialize in production or when DSN is configured
  const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
  
  if (!SENTRY_DSN) {
    console.log("Sentry DSN not configured. Error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Environment
    environment: import.meta.env.MODE,
    
    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || "1.0.0",
    
    // Filter out sensitive information
    beforeSend(event) {
      // Remove sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.filter(
          (breadcrumb) => !breadcrumb.message?.includes("password")
        );
      }
      return event;
    },
  });
};

// Capture custom errors
export const captureError = (error: any, context?: Record<string, any>) => {
  if (context) {
    Sentry.setContext("additional_context", context);
  }
  // Convert to Error if it's not already
  const errorObj = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(errorObj);
};

// Capture custom messages
export const captureMessage = (message: string, level: Sentry.SeverityLevel = "info") => {
  Sentry.captureMessage(message, level);
};

// Set user context for better error tracking
export const setUserContext = (userId: string, email?: string, username?: string) => {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
};

// Clear user context on logout
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Add breadcrumb for tracking user actions
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};
